// store/modules/user.js
import UserAPI from "@/api/user";

export default {
  namespaced: true,

  state: {
    userInfo: {
      id: null,
      username: "",
      nickname: "",
      avatar: "",
      level: 1,
      totalWords: 0,
      masteredWords: 0,
      email: "",
      registerDate: null,
      coins: 0,
      continuousStudyDays: 0,
      role: "parent", // 默认角色，可为: student | parent | admin
    },
    isLoggedIn: false,
    token: null,
  },

  mutations: {
    SET_USER_INFO(state, userInfo) {
      state.userInfo = { ...state.userInfo, ...userInfo };
    },

    SET_LOGIN_STATUS(state, status) {
      state.isLoggedIn = status;
    },

    SET_TOKEN(state, token) {
      state.token = token;
    },

    CLEAR_USER_DATA(state) {
      state.userInfo = {
        id: null,
        username: "",
        nickname: "",
        avatar: "",
        level: 1,
        totalWords: 0,
        masteredWords: 0,
        email: "",
        registerDate: null,
        coins: 0,
        continuousStudyDays: 0,
        role: "parent",
      };
      state.isLoggedIn = false;
      state.token = null;
    },

    UPDATE_WORD_STATS(state, { totalWords, masteredWords }) {
      state.userInfo.totalWords = totalWords;
      state.userInfo.masteredWords = masteredWords;
    },
  },

  actions: {
    // 登录
    async login({ commit }, { username, password, rememberMe }) {
      try {
        console.log("🔐 开始登录流程:", { username, password: "***" });

        // 调用真实的登录API
        const response = await UserAPI.login({ username, password });
        console.log("🔐 登录API响应:", {
          success: response.success,
          message: response.message,
          dataKeys: Object.keys(response.data || {}),
          fullResponse: response,
        });

        if (response.success) {
          // 处理API返回的数据结构
          const userinfo = response.data.userinfo || response.data.user;

          console.log("🔐 登录数据解析:", {
            hasUserinfo: !!userinfo,
            userinfoKeys: userinfo ? Object.keys(userinfo) : [],
            userinfoId: userinfo?.id,
            fullUserinfo: userinfo,
          });

          // 验证数据有效性
          if (!userinfo || !userinfo.id) {
            console.error("❌ 登录数据无效:", {
              userinfo,
              responseData: response.data,
            });
            return { success: false, message: "登录数据无效" };
          }

          // FastAdmin的getUserinfo()已经包含了token信息
          const user = userinfo;
          const token = user.token;

          console.log("🔐 Token提取详情:", {
            userKeys: Object.keys(user),
            hasToken: "token" in user,
            tokenValue: user.token,
            tokenType: typeof user.token,
          });

          // 更新状态
          commit("SET_USER_INFO", user);
          commit("SET_LOGIN_STATUS", true);
          commit("SET_TOKEN", token);

          // 持久化存储
          localStorage.setItem("userToken", token);
          localStorage.setItem("userInfo", JSON.stringify(user));

          // 验证保存是否成功
          const savedToken = localStorage.getItem("userToken");
          const savedUserInfo = localStorage.getItem("userInfo");

          console.log("💾 localStorage保存验证:", {
            tokenSaved: !!savedToken,
            tokenMatch: savedToken === token,
            userInfoSaved: !!savedUserInfo,
            userInfoParsable: (() => {
              try {
                const parsed = JSON.parse(savedUserInfo);
                return !!parsed.id;
              } catch (e) {
                return false;
              }
            })(),
          });

          if (rememberMe) {
            localStorage.setItem("rememberedUser", username);
          }

          return { success: true, message: response.message };
        } else {
          return { success: false, message: response.message };
        }
      } catch (error) {
        console.error("登录错误:", error);
        return { success: false, message: "登录失败，请稍后重试" };
      }
    },

    // 退出登录
    async logout({ commit }) {
      try {
        // 调用退出登录API
        await UserAPI.logout();
      } catch (error) {
        console.error("退出登录错误:", error);
      } finally {
        // 无论API调用是否成功，都清除本地状态
        commit("CLEAR_USER_DATA");

        // 清除本地存储
        localStorage.removeItem("userToken");
        localStorage.removeItem("userInfo");
      }
    },

    // 初始化用户信息（页面刷新时恢复登录状态）
    initializeAuth({ commit }) {
      const token = localStorage.getItem("userToken");
      const userInfoStr = localStorage.getItem("userInfo");

      if (token && userInfoStr) {
        try {
          const userInfo = JSON.parse(userInfoStr);
          commit("SET_USER_INFO", userInfo);
          commit("SET_LOGIN_STATUS", true);
          commit("SET_TOKEN", token);
        } catch (error) {
          // 如果解析失败，清除无效数据
          localStorage.removeItem("userToken");
          localStorage.removeItem("userInfo");
        }
      }
    },

    // 更新用户信息
    updateUserInfo({ commit }, userInfo) {
      commit("SET_USER_INFO", userInfo);

      // 同步到本地存储
      const currentUserInfo = localStorage.getItem("userInfo");
      if (currentUserInfo) {
        const updatedUserInfo = { ...JSON.parse(currentUserInfo), ...userInfo };
        localStorage.setItem("userInfo", JSON.stringify(updatedUserInfo));
      }
    },

    // 更新单词学习统计
    updateWordStats({ commit }, stats) {
      commit("UPDATE_WORD_STATS", stats);

      // 同步到本地存储
      const currentUserInfo = localStorage.getItem("userInfo");
      if (currentUserInfo) {
        const userInfo = JSON.parse(currentUserInfo);
        userInfo.totalWords = stats.totalWords;
        userInfo.masteredWords = stats.masteredWords;
        localStorage.setItem("userInfo", JSON.stringify(userInfo));
      }
    },

    // 获取用户资料
    async getUserProfile({ commit }) {
      try {
        const response = await UserAPI.getProfile();
        
        if (response.success && response.data) {
          // 更新用户信息到 store
          commit("SET_USER_INFO", response.data);
          
          // 同步到本地存储
          const currentUserInfo = localStorage.getItem("userInfo");
          if (currentUserInfo) {
            const userInfo = JSON.parse(currentUserInfo);
            const updatedUserInfo = { ...userInfo, ...response.data };
            localStorage.setItem("userInfo", JSON.stringify(updatedUserInfo));
          }
          
          return { success: true, data: response.data };
        } else {
          return { success: false, message: response.message || '获取用户资料失败' };
        }
      } catch (error) {
        console.error('获取用户资料失败:', error);
        throw error;
      }
    },

    // 更新用户资料
    async updateProfile({ commit }, userData) {
      try {
        const response = await UserAPI.updateProfile(userData);
        
        if (response.success) {
          // 更新用户信息到 store
          commit("SET_USER_INFO", userData);
          
          // 同步到本地存储
          const currentUserInfo = localStorage.getItem("userInfo");
          if (currentUserInfo) {
            const userInfo = JSON.parse(currentUserInfo);
            const updatedUserInfo = { ...userInfo, ...userData };
            localStorage.setItem("userInfo", JSON.stringify(updatedUserInfo));
          }
          
          return { success: true, message: response.message || '更新成功' };
        } else {
          return { success: false, message: response.message || '更新失败' };
        }
      } catch (error) {
        console.error('更新用户资料失败:', error);
        throw error;
      }
    },
  },

  getters: {
    isAuthenticated: (state) => state.isLoggedIn && !!state.token,
    userLevel: (state) => state.userInfo.level,
    masteryPercentage: (state) => {
      if (state.userInfo.totalWords === 0) return 0;
      return Math.round(
        (state.userInfo.masteredWords / state.userInfo.totalWords) * 100
      );
    },
    userDisplayName: (state) =>
      state.userInfo.nickname || state.userInfo.username,
    userRole: (state) => state.userInfo.role || "student",
    rememberedUsername: () => localStorage.getItem("rememberedUser") || "",
  },
};
